import { Children, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CategorizedItem {
  categoryId?: string | null;
  categoryName?: string;
}

export interface CategoryGroup<T> {
  id: string;
  name: string;
  items: T[];
}

export function groupItemsByCategory<T extends CategorizedItem>(items: T[]): CategoryGroup<T>[] {
  const groups = new Map<string, CategoryGroup<T>>();

  items.forEach((item) => {
    const name = String(item.categoryName || '').trim() || 'General';
    const id = String(item.categoryId || '').trim() || `category-${name.toLowerCase()}`;
    const existingGroup = groups.get(id);

    if (existingGroup) {
      existingGroup.items.push(item);
    } else {
      groups.set(id, { id, name, items: [item] });
    }
  });

  return Array.from(groups.values()).sort((left, right) => left.name.localeCompare(right.name));
}

interface CategoryCardCarouselProps {
  title: string;
  itemCount: number;
  itemLabel: string;
  children: ReactNode;
}

export const CategoryCardCarousel = ({
  title,
  itemCount,
  itemLabel,
  children,
}: CategoryCardCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    setCanScrollLeft(track.scrollLeft > 2);
    setCanScrollRight(track.scrollLeft < maximumScroll - 2);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const frame = window.requestAnimationFrame(updateScrollState);
    track.addEventListener('scroll', updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(track);

    return () => {
      window.cancelAnimationFrame(frame);
      track.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [itemCount, updateScrollState]);

  const scroll = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;

    track.scrollBy({
      left: direction * Math.max(track.clientWidth * 0.85, 260),
      behavior: 'smooth',
    });
  };

  const countLabel = `${itemCount} ${itemLabel}${itemCount === 1 ? '' : 's'}`;

  return (
    <section className="category-carousel" aria-labelledby={`category-heading-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="category-carousel-heading">
        <h2 id={`category-heading-${title.replace(/\s+/g, '-').toLowerCase()}`}>{title}</h2>
        <span>{countLabel}</span>
      </div>

      <div className="category-carousel-frame">
        <button
          type="button"
          className="category-carousel-button category-carousel-button--left"
          onClick={() => scroll(-1)}
          disabled={!canScrollLeft}
          aria-label={`Scroll ${title} left`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div ref={trackRef} className="category-carousel-track">
          {Children.map(children, (child) => (
            <div className="category-carousel-item">{child}</div>
          ))}
        </div>

        <button
          type="button"
          className="category-carousel-button category-carousel-button--right"
          onClick={() => scroll(1)}
          disabled={!canScrollRight}
          aria-label={`Scroll ${title} right`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
};
